import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import {
  Button,
  ButtonGroup,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import RestoreTwoToneIcon from "@mui/icons-material/RestoreTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "@mui/styles";
import { debounce, get, pickBy, map, identity } from "lodash";
import moment from "moment";
import clsx from "clsx";
import { getApplicationTab, ERRORS_MESSAGE, DEFAULT_QUOTE_FILTERS, SUCCESS_MESSAGE } from "../../constants";
import {
  getQuoteList,
  quoteSelector,
  quotesSelector,
  setQuoteFilterDate,
  saveValue as saveQuoteValue,
} from "../../store/slices/quoteSlice";
import { userSelector } from "../../store/slices/userSlice";
import { saveValue } from "../../store/slices/applicationsSlice";
import { updateApplicationStatus } from "../../store/slices/applicationFormSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import DataGridTable from "../dataGridTable/DataGridTable";
import ConfirmationDialog from "../customComponents/ConfirmationDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
};

const defaultColumns = [
  {
    field: "humanId",
    headerName: "ID",
    type: "string",
    width: 90,
    editable: false,
    sortable: false,
  },
  {
    field: "startDate",
    headerName: "Date",
    type: "string",
    width: 80,
    editable: false,
    filterable: false,
    renderCell: (params) => (
      <div>
        <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
        <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography>
      </div>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    type: "string",
    width: 140,
    editable: false,
    sortable: false,
  },
  {
    field: "applicationType",
    headerName: "Application Type",
    type: "string",
    width: 130,
    editable: false,
  },
  {
    field: "broker",
    headerName: "Broker",
    type: "string",
    width: 140,
    editable: false,
    renderCell: (params) => (
      <div>
        <Typography style={{ fontSize: "14px" }}>
          {params.value.firstName} {params.value.lastName}
        </Typography>
        <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.org}</Typography>
      </div>
    ),
  },
  {
    field: "assetType",
    headerName: "Asset type",
    type: "string",
    width: 200,
    editable: false,
    renderCell: (params) => (
      <div>
        <Typography style={{ fontSize: "14px", textWrap: "initial" }}>{params.value}</Typography>
      </div>
    ),
  },
  {
    field: "assetValue",
    headerName: "Asset value",
    type: "string",
    width: 130,
    editable: false,
  },
  {
    field: "customer",
    headerName: "Customer",
    width: 220,
    editable: false,
    sortable: false,
    renderCell: (params) => (
      <div>
        <Typography style={{ fontSize: "14px", textWrap: "initial" }}>{params.value}</Typography>
      </div>
    ),
  },
];

export default function QuotesList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSizeRef = React.useRef(10);
  const { filterDate, gettingAllQuotes, statusFilter } = useSelector(quoteSelector);
  const quotes = useSelector(quotesSelector);
  const { page } = quotes || {};
  const [searchTerm, setSearchTerm] = React.useState("");
  const [rows, setRows] = useState([]);
  const [params, setParams] = useState({ offset: 1 });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [dialogHandleFunction, setDialogHandleFunction] = useState(() => {});
  const [quoteHumanIdToUpdate, setQuoteHumanIdToUpdate] = useState("");
  const deletedQuotePreviousStatusesRef = React.useRef({});

  const localStatusFilterRef = React.useRef(statusFilter || "All");
  const filterOptions = DEFAULT_QUOTE_FILTERS;

  const handle = {
    getAllQuotesFn: ({ cursor = 1, search, startDate, endDate, filter = "", isClearDateSelection = false }) => {
      const updatedStartDate = startDate ? startDate : moment(filterDate?.startDate).format("YYYY-MM-DD");
      const updatedEndDate = endDate ? endDate : moment(filterDate?.endDate).format("YYYY-MM-DD");

      dispatch(
        getQuoteList({
          // userId: user._id,
          cursor,
          search,
          limit: pageSizeRef?.current || 10, // params?.limit
          requestedList: getApplicationTab.quote,
          filter,
          columnFilter: params?.columnFilter,
          sort: params?.sort,
          startDate:
            isClearDateSelection || !updatedStartDate || updatedStartDate === "Invalid date"
              ? undefined
              : updatedStartDate,
          endDate:
            isClearDateSelection || !updatedEndDate || updatedEndDate === "Invalid date" ? undefined : updatedEndDate,
        }),
      );
    },
    handleApplicationSearch: (searchTerm) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllQuotesFn({
          cursor: params?.offset,
          search: searchTerm,
          filter: localStatusFilterRef.current,
        });
    },
    handleFilterChange: (option) => {
      const updatedFilter = option === "Deleted" ? "QuoteDeleted" : option;
      handle.getAllQuotesFn({
        cursor: 1,
        search: searchTerm,
        filter: updatedFilter,
      });
      dispatch(saveQuoteValue({ statusFilter: option }));
      localStatusFilterRef.current = updatedFilter;
    },
    updateQuoteStatus: ({ applicationId, status, previousStatus }) => {
      dispatch(updateApplicationStatus({ applicationId, status, previousStatus })).then((res) => {
        if (!res?.error) {
          handle.getAllQuotesFn({
            cursor: params?.offset,
            search: searchTerm,
            filter: localStatusFilterRef.current,
          });

          dispatch(
            openSnackbar({
              message: SUCCESS_MESSAGE.quoteStatusUpdatedSuccessMsg,
              intent: "success",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
      setDialogHandleFunction(() => {});
    },
    onChangeDatePicker: (dates) => {
      const startDate = moment(dates?.[0]).format("YYYY-MM-DD");
      const endDate = moment(dates?.[1]).format("YYYY-MM-DD");

      dispatch(setQuoteFilterDate({ startDate: dates?.[0], endDate: dates?.[1] }));

      if ((dates[0] && dates[1]) || (!dates[0] && !dates[1])) {
        handle.getAllQuotesFn({
          cursor: 1,
          search: searchTerm,
          filter: localStatusFilterRef.current,
          startDate: startDate === "Invalid date" ? undefined : startDate,
          endDate: endDate === "Invalid date" ? undefined : endDate,
          isClearDateSelection: dates?.[0] === null && dates?.[1] === null ? true : false,
        });
      }
    },
  };

  useEffect(() => {
    if (params?.offset) {
      handle.getAllQuotesFn({
        cursor: params?.offset,
        search: searchTerm,
        filter: statusFilter,
      });
      dispatch(saveValue({ page: params?.offset, sort: params?.sort, limit: params?.limit }));
    }
  }, [params?.offset, params?.sort, params?.limit, params?.columnFilter]);

  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCountState` from being undefined during the loading
  const [rowCountState, setRowCountState] = useState(quotes?.totalDocs || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState) => quotes?.totalDocs ?? prevRowCountState);
  }, [quotes?.totalDocs]);

  useEffect(() => {
    const transformApplication = (doc) => {
      const assetValueCheck = doc?.asset?.assetValue
        ? get(doc, "asset.assetValue").toLocaleString("en-US", dollarStringOptions)
        : "No Value";

      const date = new Date(get(doc, "createdAt"));
      const entityName = get(doc, "entities[0].entityName");
      const firstName = get(doc, "customers[0].firstName", "No");
      const lastName = get(doc, "customers[0].lastName", "customer");
      const assetType = get(doc, "asset.assetType", "Type not entered");
      const assetValue = assetValueCheck;

      const capitalizedStatus = get(doc, "status", "").charAt(0).toUpperCase() + get(doc, "status", "").slice(1);
      const capitalizedApplicationType =
        get(doc, "applicationType", "").charAt(0).toUpperCase() + get(doc, "applicationType", "").slice(1);

      return pickBy(
        {
          humanId: get(doc, "humanId"),
          customer: entityName ? entityName : `${firstName} ${lastName}`,
          applicationType: capitalizedApplicationType,
          assetType,
          assetValue,
          loanAmount: get(doc, "loanDetails.loanAmount"),
          term: get(doc, "loanDetails.term"),
          id: get(doc, "_id"),
          startDate: {
            date: moment(date).format("D-M-YY"),
            time: moment(date).format("h:mm a"),
          },
          broker: {
            firstName: get(doc, "user.firstName"),
            lastName: get(doc, "user.lastName"),
            org: get(doc, "user.organisation[0].name"),
          },
          status: capitalizedStatus,
          previousStatus: get(doc, "previousStatus"),
          rawStatus: get(doc, "status"),
        },
        identity,
      );
    };

    const transformedRows = map(get(quotes, "docs"), transformApplication);
    setRows(transformedRows);
    // time: moment(date).format("h:mm a"),
  }, [quotes]);

  const debounced = useCallback(debounce(handle.handleApplicationSearch, 500), []);

  const columns = [
    ...defaultColumns,
    {
      field: "action",
      headerName: "Action",
      width: 100,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <ButtonGroup variant="text" color="primary" aria-label="action button group">
            {/^quote( submitted)?$/i.test(params?.row?.status) && (
              <Tooltip title="Delete" arrow>
                <Button
                  color="error"
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    const previousStatus = params?.row?.rawStatus || params?.row?.status || "Quote";
                    deletedQuotePreviousStatusesRef.current[params?.id] = previousStatus;
                    setDialogHandleFunction(
                      () => () =>
                        handle.updateQuoteStatus({ applicationId: params?.id, status: "QuoteDeleted", previousStatus }),
                    );
                    setQuoteHumanIdToUpdate(params?.row?.humanId);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteTwoToneIcon />
                </Button>
              </Tooltip>
            )}
            {/^quotedeleted$/i.test(params?.row?.status) && (
              <Tooltip title="Restore" arrow>
                <Button
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    const status =
                      params?.row?.previousStatus || deletedQuotePreviousStatusesRef.current[params?.id] || "Quote";
                    setDialogHandleFunction(
                      () => () => handle.updateQuoteStatus({ applicationId: params?.id, status }),
                    );
                    setQuoteHumanIdToUpdate(params?.row?.humanId);
                    setRestoreDialogOpen(true);
                  }}
                >
                  <RestoreTwoToneIcon color="primary" />
                </Button>
              </Tooltip>
            )}
          </ButtonGroup>
        </Stack>
      ),
    },
  ];

  return (
    <Grid container item direction="column" spacing={0} style={{ padding: `20px` }}>
      <Grid
        item
        style={{
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          alignItems: "center",
          columnGap: "40px",
        }}
      >
        <Typography
          variant="h1"
          style={{
            fontSize: "24px",
            fontWeight: 800,
            letterSpacing: "-0.5px",
          }}
        >
          Quotes
        </Typography>
        <div className={classes.root}>
          <FormControl className={clsx(classes.margin, classes.textField)}>
            <Input
              className="input-border-bottom"
              id="application-search"
              autoComplete={false}
              variant="filled"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debounced(e.target.value, 1000);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        searchTerm !== "" && debounced("", 1000);
                      }}
                      color="secondary"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </FormControl>
        </div>

        <ButtonGroup variant="outlined">
          {filterOptions.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleFilterChange(option);
              }}
              variant={statusFilter === option ? "contained" : "outlined"}
            >
              {option}
            </Button>
          ))}
        </ButtonGroup>

        <Stack style={{ marginBottom: "7px" }}>
          <DatePicker
            selected={filterDate?.startDate}
            onChange={handle.onChangeDatePicker}
            startDate={filterDate?.startDate}
            endDate={filterDate?.endDate}
            selectsRange
            isClearable
            name="Select Date"
            monthsShown={2}
            maxDate={new Date()} // Disable future dates
            customInput={<TextField label="Select Date" size="small" />}
          />
        </Stack>
      </Grid>

      <Grid item>
        <DataGridTable
          data={rows || []}
          columns={columns}
          onRowClick={(event) => {
            navigate(`/quote/${event.id}`);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={pageSizeRef?.current || 10}
          rowCount={rowCountState}
          params={params}
          setParams={setParams}
          page={page}
          isLoading={gettingAllQuotes}
          pageSizeRef={pageSizeRef}
        />
      </Grid>
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        handle={dialogHandleFunction}
        setIsOpen={setDeleteDialogOpen}
        title={
          <span>
            Are you sure you want to <span style={{ color: red[500] }}>DELETE</span> quote
            {quoteHumanIdToUpdate ? ` ${quoteHumanIdToUpdate}` : ""}?
          </span>
        }
        proceedBtnColor={"error"}
        proceedBtnText={"Delete"}
      />
      <ConfirmationDialog
        isOpen={restoreDialogOpen}
        handle={dialogHandleFunction}
        setIsOpen={setRestoreDialogOpen}
        title={
          <span>
            Are you sure you want to <span style={{ color: green[500] }}>RESTORE</span> application
            {quoteHumanIdToUpdate ? ` ${quoteHumanIdToUpdate}` : ""}?
          </span>
        }
        proceedBtnColor={"primary"}
        proceedBtnText={"Restore"}
      />
    </Grid>
  );
}
